import {
  Button,
  Card,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  Modal,
  Radio,
  Row,
  Spin,
  Typography,
  message,
  Space,
} from "antd";
import {
  CheckCircleFilled,
  CheckOutlined,
  CloseOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import GoogleAdsLogo from "../icons/GoogleAdsLogo";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useState, useCallback } from "react";
import { fetchOrganzation, updateOrganization } from "../../redux/organizationSlice";
import axiosInstance from "../../redux/apiAxios";

const { Title, Text } = Typography;
const { Item } = Form;

/** Replaces antd vertical Steps — clearer spacing and status styling in the Google Ads card. */
function GoogleAdsSetupProgress({ oauth_connected, customer_id, setup_completed }) {
  const items = [
    {
      title: "Sign in",
      description: "Google OAuth",
      status: oauth_connected ? "finish" : "wait",
    },
    {
      title: "Ads account",
      description: "Customer ID",
      status: customer_id ? "finish" : oauth_connected ? "process" : "wait",
    },
    {
      title: "Conversion",
      description: "autobot_bookings",
      status: setup_completed ? "finish" : customer_id ? "process" : "wait",
    },
  ];

  return (
    <div role="list" aria-label="Google Ads setup steps">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const { status, title, description } = item;
        const isDone = status === "finish";
        const isActive = status === "process";
        const isPending = !isDone && !isActive;

        return (
          <Flex key={title} align="flex-start" gap={10} wrap="nowrap" role="listitem">
            <Flex vertical align="center" style={{ width: 22, flexShrink: 0 }}>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  background: isDone ? "#52c41a" : isActive ? "rgba(22, 119, 255, 0.1)" : "#fff",
                  border: isDone
                    ? "none"
                    : isActive
                    ? "2px solid #1677ff"
                    : "2px solid rgba(0, 0, 0, 0.12)",
                  color: "#fff",
                  boxSizing: "border-box",
                }}
                aria-hidden
              >
                {isDone ? (
                  <CheckOutlined style={{ fontSize: 10 }} />
                ) : isActive ? (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#1677ff",
                    }}
                  />
                ) : (
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: "rgba(0, 0, 0, 0.15)",
                    }}
                  />
                )}
              </div>
              {!isLast && (
                <div
                  style={{
                    width: 2,
                    minHeight: 20,
                    marginTop: 4,
                    marginBottom: 2,
                    borderRadius: 1,
                    background: isDone ? "#95de64" : "rgba(0, 0, 0, 0.08)",
                  }}
                  aria-hidden
                />
              )}
            </Flex>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                paddingBottom: isLast ? 2 : 14,
                paddingTop: 2,
              }}
            >
              <Text
                strong
                style={{
                  fontSize: 14,
                  display: "block",
                  lineHeight: 1.35,
                  color: isPending ? "rgba(0, 0, 0, 0.45)" : "rgba(0, 0, 0, 0.88)",
                }}
              >
                {title}
              </Text>
              <Text
                type="secondary"
                style={{
                  fontSize: 12,
                  display: "block",
                  marginTop: 4,
                  lineHeight: 1.45,
                  color: isPending ? "rgba(0, 0, 0, 0.38)" : undefined,
                }}
              >
                {description}
              </Text>
            </div>
          </Flex>
        );
      })}
    </div>
  );
}

const ThirdPartyInfo = ({ isNewOrg = false }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [googleAdsCustomers, setGoogleAdsCustomers] = useState([]);
  const [googleAdsModalLoading, setGoogleAdsModalLoading] = useState(false);
  const [googleAdsPrimaryLoading, setGoogleAdsPrimaryLoading] = useState(false);
  const [googleAdsModalOpen, setGoogleAdsModalOpen] = useState(false);
  const [googleAdsDisconnectLoading, setGoogleAdsDisconnectLoading] = useState(false);
  const [googleAdsSelectedId, setGoogleAdsSelectedId] = useState("");
  const [googleAdsManualId, setGoogleAdsManualId] = useState("");
  const { organizationData, loading } = useSelector(
    (state) => state?.organization
  );

  const initialValues = useMemo(
    () => ({
      st_tenant_id: "",
      st_client_id: "",
      st_client_secret: "",
      st_app_key: "",
      st_refresh_token: "",
      ghl_api_key: "",
      whatConverts_profileId: "",
      whatConverts_api_key: "",
      whatConverts_secret: "",
      zapier_webhook: "",
      measurement_id: "",
      api_secret: "",
    }),
    []
  );

  useEffect(() => {
    if (organizationData) {
      Object.keys(initialValues).map((item) => {
        return form.setFieldValue(`${item}`, organizationData?.apiKeys[item]);
      });
    }
  }, [form, initialValues, organizationData]);

  useEffect(() => {
    const raw = sessionStorage.getItem("googleAdsPickCustomers");
    if (!raw) return;
    try {
      const list = JSON.parse(raw);
      if (Array.isArray(list)) {
        setGoogleAdsCustomers(list);
        setGoogleAdsSelectedId(list[0]?.customerId || "");
        setGoogleAdsManualId("");
      }
    } catch {
      /* ignore */
    }
    sessionStorage.removeItem("googleAdsPickCustomers");
  }, [organizationData?._id]);

  const handleSubmit = (formValues) => {
    const payload = { apiKeys: formValues };
    dispatch(updateOrganization({ id: organizationData._id, payload }));
  };

  const handleGetTenantId = async () => {
    const apiKey = form.getFieldValue("st_app_key");
    if (!apiKey) {
      message.error("Please enter API Key first");
      return;
    }
    const companyData = await axiosInstance.get(
      `/get/token?key=${apiKey}&orgId=${organizationData?._id}`
    );
    form.setFieldValue("st_tenant_id", companyData?.data);
  };

  const isHCP = organizationData?.organization_provider === "HCP";
  const googleAds = organizationData?.google_ads;

  const loadGoogleAdsCustomers = useCallback(async () => {
    if (!googleAds?.oauth_connected) return [];
    setGoogleAdsModalLoading(true);
    setGoogleAdsCustomers([]);
    try {
      const res = await axiosInstance.get("/google-ads/accessible-customers");
      const list = res?.data?.customers || [];
      setGoogleAdsCustomers(list);
      return list;
    } catch {
      setGoogleAdsCustomers([]);
      return [];
    } finally {
      setGoogleAdsModalLoading(false);
    }
  }, [googleAds?.oauth_connected]);

  const openGoogleAdsLinkModal = async () => {
    if (!googleAds?.oauth_connected) {
      message.warning("Use the main Google Ads button first to sign in.");
      return;
    }
    setGoogleAdsModalOpen(true);
    setGoogleAdsManualId("");
    const list = await loadGoogleAdsCustomers();
    const current = organizationData?.google_ads?.customer_id || "";
    const ids = list.map((c) => c.customerId);
    if (current && ids.includes(current)) {
      setGoogleAdsSelectedId(current);
    } else if (list.length === 1) {
      setGoogleAdsSelectedId(list[0].customerId);
    } else {
      setGoogleAdsSelectedId(ids[0] || "");
    }
    if (current && !ids.includes(current)) {
      setGoogleAdsManualId(current);
    }
  };

  const startGoogleAdsOAuth = async () => {
    try {
      const res = await axiosInstance.get("/google-ads/oauth/authorize-url");
      const url = res?.data?.url;
      if (!url) {
        message.error("Could not start Google Ads authorization");
        return;
      }
      window.location.href = url;
    } catch (e) {
      message.error(e?.response?.data?.message || e.message);
    }
  };

  const runGoogleAdsAutoSetup = async () => {
    try {
      setGoogleAdsPrimaryLoading(true);
      const { data } = await axiosInstance.post("/google-ads/setup/auto");
      if (data?.ok && data?.step === "complete") {
        message.success(
          data?.message ||
            "Google Ads conversions are enabled for bookings with click IDs."
        );
        await dispatch(fetchOrganzation(organizationData._id));
        return;
      }
      if (data?.step === "need_choice") {
        setGoogleAdsCustomers(data.customers || []);
        setGoogleAdsSelectedId(data.customers?.[0]?.customerId || "");
        setGoogleAdsModalOpen(true);
        message.info("Choose which Google Ads account to use.");
        return;
      }
      if (data?.step === "need_customer") {
        setGoogleAdsModalOpen(true);
        setGoogleAdsCustomers([]);
        message.warning(data?.message || "Enter your Google Ads customer ID.");
        return;
      }
    } catch (e) {
      message.error(e?.response?.data?.message || e.message);
    } finally {
      setGoogleAdsPrimaryLoading(false);
    }
  };

  const handleGoogleAdsPrimaryClick = async () => {
    if (!googleAds?.oauth_connected) {
      await startGoogleAdsOAuth();
      return;
    }
    if (!googleAds?.setup_completed) {
      await runGoogleAdsAutoSetup();
    }
  };

  const clearGoogleAdsConnection = () => {
    Modal.confirm({
      title: "Remove Google Ads connection?",
      content:
        "This disconnects Google for this organization: OAuth tokens, linked customer ID, and conversion action settings are cleared. You can connect again anytime.",
      okText: "Remove connection",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setGoogleAdsDisconnectLoading(true);
          await axiosInstance.post("/google-ads/disconnect");
          message.success("Google Ads connection removed.");
          await dispatch(fetchOrganzation(organizationData._id));
        } catch (e) {
          message.error(e?.response?.data?.message || e.message);
          throw e;
        } finally {
          setGoogleAdsDisconnectLoading(false);
        }
      },
    });
  };

  const linkGoogleAdsCustomerFromModal = async () => {
    const manual = googleAdsManualId?.replace(/\D/g, "").trim();
    const fromList = googleAdsSelectedId?.replace(/\D/g, "").trim();
    const customer_id = manual || fromList;
    if (!customer_id) {
      message.error("Select an account or enter a customer ID");
      return;
    }
    try {
      setGoogleAdsModalLoading(true);
      await axiosInstance.post("/google-ads/link-customer", {
        customer_id,
        login_customer_id: "",
      });
      const { data } = await axiosInstance.post("/google-ads/setup/auto");
      setGoogleAdsModalOpen(false);
      if (data?.ok && data?.step === "complete") {
        message.success(data?.message || "Google Ads setup complete.");
      } else {
        message.warning(
          data?.message || "Account linked — press Complete setup if conversions aren’t active yet."
        );
      }
      await dispatch(fetchOrganzation(organizationData._id));
    } catch (e) {
      message.error(e?.response?.data?.message || e.message);
    } finally {
      setGoogleAdsModalLoading(false);
    }
  };

  return (
    <>
      <Spin spinning={loading} fullscreen />
      <Flex align="center" gap={8}>
        <KeyOutlined style={{ fontSize: 17 }} />
        <Title level={3} style={{ margin: 0 }}>
          APIs Integration Keys
        </Title>
      </Flex>

      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleSubmit}
        layout="vertical"
        labelCol={{
          span: 6,
        }}
        wrapperCol={{
          span: 20,
        }}
        autoComplete="new-state"
      >
        <Row>
          <Col span={10}>
            <Divider
              orientation="left"
              orientationMargin={0}
              style={{ marginTop: "40px", marginBottom: "10px" }}
            >
              <Text
                strong
                underline
                style={{
                  color: isHCP ? "#2378cf" : "rgba(0, 0, 0, 0.45)",
                }}
              >
                {isHCP ? "HouseCallPro Details" : "Service Titan Details"}
              </Text>
            </Divider>

            {isHCP ? (
              <>
                <Item name="st_app_key" label="API Key">
                  <Input
                    placeholder="Enter your HouseCallPro API Key"
                    size="large"
                    style={{ borderRadius: '6px' }}
                  />
                </Item>

                <Item name="st_tenant_id" label="Tenant ID">
                  <Input
                    placeholder="Your HouseCallPro Tenant ID"
                    size="large"
                    style={{ borderRadius: '6px' }}
                  />
                </Item>

                <div >
                  {!organizationData?.apiKeys?.st_tenant_id && (
                    <Button
                      type="primary"
                      onClick={handleGetTenantId}
                      size="large"
                      style={{
                        background: '#2378cf',
                        borderRadius: '6px',
                        minWidth: '80px'
                      }}
                    >
                      GET
                    </Button>
                  )}
                </div>

                <Item style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    Enter your API Key and click GET to fetch your Tenant ID automatically
                  </Text>
                </Item>
              </>
            ) : (
              <>
                <Item name="st_app_key" label="App Key">
                  <Input placeholder="App Key" size="large" />
                </Item>

                <Item name="st_client_id" label="Client ID">
                  <Input placeholder="Client ID" size="large" />
                </Item>

                <Item name="st_client_secret" label="Client Secret">
                  <Input placeholder="Client Secret" size="large" />
                </Item>

                <Item name="st_tenant_id" label="Tenant ID">
                  <Input placeholder="Tenant ID" size="large" />
                </Item>
              </>
            )}
          </Col>

          <Col span={10} offset={1}>
            <Divider
              orientation="left"
              orientationMargin={0}
              style={{ marginTop: "40px", marginBottom: "10px" }}
            >
              <Text
                type="secondary"
                strong
                underline
                style={{ marginBottom: "10px" }}
              >
                WebHook Details
              </Text>
            </Divider>

            <Item name="zapier_webhook" label="Web Hook">
              <Input
                placeholder="Web Hook Url (e.g. Make / Zapier)"
                size="large"
              />
            </Item>

            <Divider
              orientation="left"
              orientationMargin={0}
              style={{ marginTop: "40px", marginBottom: "10px" }}
            >
              <Text
                type="secondary"
                strong
                underline
                style={{ marginBottom: "10px" }}
              >
                WhatConverts Details
              </Text>
            </Divider>

            <Item name="whatConverts_profileId" label="Profile ID">
              <Input placeholder="WhatConverts Profile ID" size="large" />
            </Item>
            <Item name="whatConverts_api_key" label="API Token">
              <Input placeholder="WhatConverts API Token" size="large" />
            </Item>

            <Item name="whatConverts_secret" label="API Secret">
              <Input placeholder="WhatConverts Secret" size="large" />
            </Item>
            <>
              <Divider
                orientation="left"
                orientationMargin={0}
                style={{ marginTop: "40px", marginBottom: "10px" }}
              >
                <Text
                  type="secondary"
                  strong
                  underline
                  style={{ marginBottom: "10px" }}
                >
                  Google Analytics Details
                </Text>
              </Divider>

              <Item name="measurement_id" label="Measurement ID">
                <Input placeholder="Google Analytics Measurement ID (e.g. G-XXXXXXXXXX)" size="large" />
              </Item>

              <Item name="api_secret" label="API Secret">
                <Input placeholder="Google Analytics API Secret" size="large" />
              </Item>

              <Divider
                orientation="left"
                orientationMargin={0}
                style={{ marginTop: "40px", marginBottom: "10px" }}
              >
                <Text type="secondary" strong underline>
                  Google Ads conversions
                </Text>
              </Divider>

              <Card
                size="small"
                style={{
                  width: "100%",
                  maxWidth: 580,
                  marginInline: "auto",
                  borderRadius: 12,
                  border: "1px solid rgba(66, 133, 244, 0.35)",
                  background:
                    "linear-gradient(165deg, rgba(66, 133, 244, 0.06) 0%, rgba(52, 168, 83, 0.04) 100%)",
                  boxShadow: "0 4px 14px rgba(15, 30, 60, 0.06)",
                }}
                styles={{ body: { padding: "22px 24px 20px" } }}
              >
                <div
                  style={{
                    maxWidth: 500,
                    margin: "0 auto",
                  }}
                >
                  <Flex align="flex-start" gap={12} style={{ marginBottom: 18 }}>
                    <div
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                        flexShrink: 0,
                      }}
                    >
                      <GoogleAdsLogo size={24} aria-hidden />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text strong style={{ fontSize: 16, display: "block", lineHeight: 1.35 }}>
                        Google Ads — booking conversions
                      </Text>
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 13,
                          display: "block",
                          marginTop: 8,
                          lineHeight: 1.55,
                        }}
                      >
                        Successful bookings can be reported as{" "}
                        <Text strong>conversions in Google Ads</Text>. Link this organization to your
                        ads customer and the <Text strong>autobot_bookings</Text> conversion action.
                        When the visit includes a click ID (gclid, wbraid, or gbraid), it is sent with
                        the conversion.
                      </Text>
                    </div>
                    {googleAds?.oauth_connected && (
                      <Button
                        type="text"
                        shape="circle"
                        loading={googleAdsDisconnectLoading}
                        icon={<CloseOutlined />}
                        onClick={clearGoogleAdsConnection}
                        aria-label="Remove Google Ads connection"
                        title="Remove Google Ads connection"
                        style={{
                          color: "rgba(0,0,0,0.45)",
                          flexShrink: 0,
                          marginTop: -4,
                        }}
                      />
                    )}
                  </Flex>

                  <div
                    style={{
                      marginBottom: 20,
                      padding: "4px 0 2px 2px",
                      borderTop: "1px solid rgba(0, 0, 0, 0.06)",
                      paddingTop: 18,
                    }}
                  >
                    <GoogleAdsSetupProgress
                      oauth_connected={!!googleAds?.oauth_connected}
                      customer_id={googleAds?.customer_id}
                      setup_completed={!!googleAds?.setup_completed}
                    />
                  </div>

                  {googleAds?.setup_completed ? (
                    <Flex align="center" gap={6} justify="center" style={{ marginBottom: 14 }}>
                      <CheckCircleFilled style={{ color: "#34A853", fontSize: 15 }} />
                      <Text strong style={{ color: "#34A853" }}>
                        Connected — customer {googleAds?.customer_id}
                      </Text>
                    </Flex>
                  ) : (
                    <Text
                      type="secondary"
                      style={{
                        display: "block",
                        marginBottom: 14,
                        textAlign: "center",
                        lineHeight: 1.5,
                      }}
                    >
                      {googleAds?.oauth_connected && !googleAds?.customer_id
                        ? "Choose which Google Ads customer account should receive these booking conversions."
                        : googleAds?.oauth_connected && googleAds?.customer_id
                        ? "Finish the conversion action in Google Ads if setup is still pending — use Complete setup below."
                        : "Connect Google and select your ads account to start sending booking conversions."}
                    </Text>
                  )}

                  <Flex
                    gap={10}
                    wrap="wrap"
                    align="center"
                    justify="center"
                    style={{ width: "100%", paddingTop: 2 }}
                  >
                    {googleAds?.setup_completed ? (
                      <Flex
                        align="center"
                        gap={10}
                        style={{
                          height: 46,
                          paddingInline: 18,
                          borderRadius: 10,
                          fontWeight: 600,
                          background: "#34A853",
                          color: "#fff",
                          boxShadow: "0 2px 8px rgba(52, 168, 83, 0.45)",
                        }}
                      >
                        Connected
                      </Flex>
                    ) : (
                      <Button
                        type="primary"
                        size="large"
                        loading={googleAdsPrimaryLoading}
                        onClick={handleGoogleAdsPrimaryClick}
                        aria-label={
                          !googleAds?.oauth_connected
                            ? "Connect Google Ads"
                            : "Complete Google Ads setup"
                        }
                      >
                        {!googleAds?.oauth_connected
                          ? "Connect Google Ads"
                          : "Complete setup"}
                      </Button>
                    )}
                    {!googleAds?.setup_completed && googleAds?.oauth_connected && (
                      <Button size="large" type="link" onClick={openGoogleAdsLinkModal}>
                        Choose account manually
                      </Button>
                    )}
                    {googleAds?.oauth_connected && (
                      <Button size="large" type="default" onClick={startGoogleAdsOAuth}>
                        Reconnect Google
                      </Button>
                    )}
                  </Flex>
                </div>
              </Card>
            </>
          </Col>
        </Row>

        <Button type="primary" htmlType="submit" size="large">
          Save
        </Button>
      </Form>

      <Modal
        title={
          <Flex align="center" gap={8}>
            <GoogleAdsLogo size={18} title="" />
            <span>Choose Google Ads account</span>
          </Flex>
        }
        open={googleAdsModalOpen}
        onCancel={() => setGoogleAdsModalOpen(false)}
        onOk={linkGoogleAdsCustomerFromModal}
        confirmLoading={googleAdsModalLoading}
        okText="Link account"
        destroyOnClose
        width={520}
      >
        <Spin spinning={googleAdsModalLoading}>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Accounts your Google login can access (from Google Ads API). Pick one to attach
            conversions to this organization.
          </Text>
          {googleAdsCustomers.length > 0 ? (
            <Radio.Group
              value={googleAdsSelectedId}
              onChange={(e) => {
                setGoogleAdsSelectedId(e.target.value);
                setGoogleAdsManualId("");
              }}
              style={{ width: "100%" }}
            >
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {googleAdsCustomers.map((c) => (
                  <Radio key={c.customerId} value={c.customerId}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {c.descriptiveName ? (
                        <Text strong>{c.descriptiveName}</Text>
                      ) : (
                        <Text strong>Google Ads Customer</Text>
                      )}
                      <Text type="secondary" style={{ marginTop: -2 }}>
                        ID: <Text strong>{c.customerId}</Text>
                      </Text>
                    </div>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          ) : !googleAdsModalLoading ? (
            <Text type="warning" style={{ display: "block", marginBottom: 12 }}>
              No accounts were returned for this Google login. Enter the customer ID from Google
              Ads (numeric ID, no dashes).
            </Text>
          ) : null}
          <Divider plain style={{ margin: "16px 0" }}>
            Or enter a customer ID manually
          </Divider>
          <Input
            placeholder="e.g. 1234567890"
            size="large"
            value={googleAdsManualId}
            onChange={(e) => {
              const v = e.target.value;
              setGoogleAdsManualId(v);
              if (v.trim()) {
                setGoogleAdsSelectedId("");
              }
            }}
            maxLength={20}
          />
        </Spin>
      </Modal>
    </>
  );
};

export default ThirdPartyInfo;
